import * as React from 'react';

import { useToast } from '@longline/aqua-ui/services/Toast/useToast';
import { TabBar } from '@longline/aqua-ui/controls/TabBar';

import { useSystemsContext } from '../ISystemsContext';
import { Pane } from '../../../ui/Pane';
import { ISystem, ISystemLayer } from '../../../api/models/ISystem';
import { SystemForm } from '../SystemForm';
import { ExcelImport } from '../../../dialogs/ExcelImport';
import { LayerManager } from '../LayerManager';
import { ILayer } from '../../../types/ILayer';
import { ELayerType } from '../../../types/ELayerType';
import { DriversetManager } from '../DriversetManager';
import { IDriverset } from '../../../api/models';

const EditSystem = () => {
  const ctx = useSystemsContext();
  const toastProvider = useToast();
  const controller = React.useRef<AbortController | null>(null);

  // The mode === 'edit' switch in SystemsManager guarantees ctx.system is
  // populated before this component is rendered.
  const initialSystem = ctx.system;
  const systemRef = React.useRef<ISystem | null>(initialSystem ? {...initialSystem} : null);
  const [system, setSystem] = React.useState<ISystem | null>(systemRef.current);
  const [isValid, setIsValid] = React.useState<boolean>(true);
  const [loading, setLoading] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  if (!systemRef.current || !system) return null;
  const sysRef = systemRef.current;

  const handleChange = (value: ISystem, forceupdate: boolean) => {
    // If country changes, then division must reset to undefined.
    if(Object.keys(value).includes('country') && sysRef.country != value.country) {
      value.division = undefined;
    }
    // Only assign to local ref, so form doesn't update.
    Object.assign(sysRef, value);
    // ... unless forceupdate, then update the local state so that the form
    // *does* update.
    if(forceupdate) {
      setSystem({...sysRef});
    }
  }

  const handleImportLayer = (layers: ILayer[]) => {
    setImporting(false);
    layers.forEach(layer => {
      sysRef.layers.push({ name: layer.source, type: layer.type, data: layer.data });
    });
    setSystem({ ...sysRef });
  } 

  const handleEditLayer = (layer: ISystemLayer, name: string) => {
    layer.name = name;
    setSystem({ ...sysRef });
  }  

  const handleDeleteLayer = (layer: ISystemLayer) => {
    sysRef.layers = sysRef.layers.filter(l => l != layer);
    setSystem({ ...sysRef });
  }

  const handleChangeDriversets = (driversets: IDriverset[]) => {
    sysRef.driversets = driversets;
    setSystem({ ...sysRef });
  }  

  const handleSubmit = async() => {
    if(loading) return;

    if(!isValid) {
      setDirty(true);
      ctx.setTab('info');
      return;
    }    

    const bathymetryCount = sysRef.layers.filter(f => f.type == ELayerType.Depth).length;
    if(bathymetryCount == 0) {
      toastProvider.toast("Please upload a bathymetry dataset.", { dismissable: true });
      ctx.setTab('datasets');
      return;
    }    
    if(bathymetryCount > 1) {
      toastProvider.toast("A system cannot have more than one bathymetry dataset.", { dismissable: true });
      ctx.setTab('datasets');
      return;
    }        
    if(sysRef.layers.filter(f => f.type == ELayerType.CurrentSpeed).length < 1) {
      toastProvider.toast("Please upload at least one current speed dataset.", { dismissable: true });
      ctx.setTab('datasets');
      return;
    }
    if(sysRef.driversets.length < 1) {
      toastProvider.toast("Please create at least one driver set.", { dismissable: true });
      ctx.setTab('driversets');
      return;
    }

    setLoading(true);
    controller.current = new AbortController();
    await ctx.updateSystem(sysRef, controller.current.signal);
    setLoading(false);
  }

  return (
    <>
      <Pane.Heading>
        <Pane.Title>{sysRef.name}</Pane.Title>
      </Pane.Heading>
      <Pane.Subheader expandable>
        Edit system
      </Pane.Subheader>

      <TabBar active={ctx.tab === 'datasets' ? 1 : (ctx.tab === 'driversets' ? 2 : 0)} line>
        <TabBar.Tab onClick={() => ctx.setTab('info')}>System info</TabBar.Tab>
        <TabBar.Tab onClick={() => ctx.setTab('datasets')} badge={system.layers.length}>Datasets</TabBar.Tab>
        <TabBar.Tab onClick={() => ctx.setTab('driversets')} badge={(system.driversets || []).length}>Driver sets</TabBar.Tab>
      </TabBar>

      <div style={{display: ctx.tab === 'info' ? 'block' : 'none', flex: 1 }}>
        <Pane.Body>
          <SystemForm
            system={system}
            onChange={handleChange}
            onValidate={(v: boolean) => setIsValid(v)}
            disabled={loading}
            dirty={dirty}
          />
        </Pane.Body> 
      </div>

      <div style={{display: ctx.tab === 'datasets' ? 'block' : 'none', flex: 1 }}>
        <LayerManager layers={[...system.layers]} onAdd={() => setImporting(true)} onDelete={handleDeleteLayer} onEdit={handleEditLayer}/>
      </div>

      <div style={{display: ctx.tab === 'driversets' ? 'block' : 'none', flex: 1 }}>
        <DriversetManager driversets={system.driversets || []} onChange={handleChangeDriversets}/>
      </div>

      <Pane.Footer>
        <Pane.CancelButton onClick={() => ctx.setMode('view')}/>
        <Pane.SubmitButton onClick={handleSubmit} working={loading} disabled={loading}/>
      </Pane.Footer>

      {importing && <ExcelImport columns={['latitude', 'longitude', 'depth', 'u', 'v']} title="Import data from Excel" onCancel={() => setImporting(false)} onImport={handleImportLayer} />}
    </>
  )
}

export { EditSystem }
